import {
  cloneElement,
  isValidElement,
  useId,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from './util';

export interface TooltipProps {
  /** Tooltip content. When null/empty, the child is rendered without a tooltip. */
  content: ReactNode;
  /** Where the bubble appears relative to the trigger. */
  side?: 'top' | 'bottom';
  /**
   * The trigger. It is wrapped in an inline-flex span that receives hover/focus; when it is a
   * single element it also gets `aria-describedby` pointing at the tooltip text.
   */
  children: ReactElement | ReactNode;
  className?: string;
  /** Extra classes for the bubble (e.g. edge alignment where a centred bubble would leave the screen). */
  bubbleClassName?: string;
  /** Force-open (for showcases / onboarding hints). */
  open?: boolean;
}

/**
 * Comic caption tooltip shown on hover and on keyboard focus (focus events bubble from the wrapped
 * control). The description text is always in the DOM (visually hidden) so `aria-describedby`
 * resolves even while the bubble is closed; Esc dismisses it (WCAG 1.4.13).
 */
export function Tooltip({
  content,
  side = 'top',
  children,
  className,
  bubbleClassName,
  open,
}: TooltipProps) {
  const id = useId();
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const empty = content == null || content === '' || content === false;
  const visible = !empty && (open ?? ((hover || focus) && !dismissed));

  if (empty) return <>{children}</>;

  const trigger = isValidElement<{ 'aria-describedby'?: string }>(children)
    ? cloneElement(children, {
        'aria-describedby': [children.props['aria-describedby'], id].filter(Boolean).join(' '),
      })
    : children;

  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Escape' && visible && open === undefined) {
      setDismissed(true);
      e.stopPropagation();
    }
  };

  return (
    <span
      className={cn('relative inline-flex', className)}
      onPointerEnter={() => {
        setDismissed(false);
        setHover(true);
      }}
      onPointerLeave={() => setHover(false)}
      onFocus={() => {
        setDismissed(false);
        setFocus(true);
      }}
      onBlur={() => setFocus(false)}
      onKeyDown={onKeyDown}
    >
      {trigger}
      <span id={id} role="tooltip" className="sr-only">
        {content}
      </span>
      <AnimatePresence>
        {visible && (
          <motion.span
            aria-hidden
            initial={{ opacity: 0, scale: 0.6, y: side === 'top' ? 6 : -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 520, damping: 26 }}
            className={cn(
              'pointer-events-none absolute left-1/2 z-50 w-max max-w-64 -translate-x-1/2 border-3 border-ink bg-white px-2.5 py-1.5 text-center text-[13px] leading-snug font-extrabold text-ink shadow-ink-sm',
              side === 'top' ? 'bottom-full mb-3 origin-bottom' : 'top-full mt-3 origin-top',
              bubbleClassName,
            )}
          >
            {content}
            <span
              aria-hidden
              className={cn(
                'absolute left-1/2 size-3 -translate-x-1/2 rotate-45 border-ink bg-white',
                side === 'top'
                  ? '-bottom-[8px] border-r-3 border-b-3'
                  : '-top-[8px] border-t-3 border-l-3',
              )}
            />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
